import { useEffect, useRef, useState } from "react";
import { ImageIcon, Loader2, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getCachedImageUrl, cacheImageFromUrl } from "@/lib/imageBlobCache";

type Props = {
  description: string;
  subject?: string;
  topic?: string;
  language?: string;
};

// ===== Client-side illustration cache (localStorage) =====
// Avoids re-invoking the edge function when the same notion illustration was
// already generated in a previous session. Server still has its own DB cache,
// but this skips even the network round-trip.
const ILLUSTRATION_CACHE_KEY = "kelas-illustration-cache-v1";
const MAX_CACHE_ENTRIES = 200;

type CacheEntry = { url: string; ts: number };
type CacheMap = Record<string, CacheEntry>;

const normalizeKey = (s: string) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

const buildCacheKey = (subject: string, topic: string, language: string) =>
  `${normalizeKey(subject)}:${normalizeKey(topic)}:${normalizeKey(language)}`;

const readCache = (): CacheMap => {
  try {
    const raw = localStorage.getItem(ILLUSTRATION_CACHE_KEY);
    return raw ? (JSON.parse(raw) as CacheMap) : {};
  } catch {
    return {};
  }
};

const writeCache = (map: CacheMap) => {
  try {
    const entries = Object.entries(map);
    if (entries.length > MAX_CACHE_ENTRIES) {
      entries.sort((a, b) => b[1].ts - a[1].ts);
      const trimmed: CacheMap = {};
      for (const [k, v] of entries.slice(0, MAX_CACHE_ENTRIES)) trimmed[k] = v;
      map = trimmed;
    }
    localStorage.setItem(ILLUSTRATION_CACHE_KEY, JSON.stringify(map));
  } catch {
    /* quota exceeded — ignore */
  }
};

const NotionIllustration = ({ description, subject = "general", topic, language = "fr" }: Props) => {
  const cacheKey = buildCacheKey(subject, topic || description, language);
  const initialRemote = (() => {
    try {
      return readCache()[cacheKey]?.url || null;
    } catch {
      return null;
    }
  })();

  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);
  const [premiumRequired, setPremiumRequired] = useState(false);
  const triggered = useRef(false);

  useEffect(() => {
    if (triggered.current) return;
    triggered.current = true;

    let cancelled = false;
    (async () => {
      // 1) Try persistent blob cache first (works offline, zero network)
      try {
        const blobUrl = await getCachedImageUrl(cacheKey);
        if (blobUrl) {
          if (!cancelled) {
            setUrl(blobUrl);
            setLoading(false);
          }
          return;
        }
      } catch { /* ignore */ }

      // 2) If we know the remote URL from a previous session, fetch + cache the blob
      if (initialRemote) {
        const blobUrl = await cacheImageFromUrl(cacheKey, initialRemote);
        if (!cancelled) {
          if (blobUrl) {
            setUrl(blobUrl);
          } else {
            setUrl(initialRemote);
          }
          setLoading(false);
        }
        return;
      }

      // 3) Otherwise, ask the edge function to generate / fetch from server cache
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData.session?.access_token;

        if (!accessToken) {
          if (!cancelled) setHidden(true);
          return;
        }

        const { data, error: fnErr } = await supabase.functions.invoke("generate-illustration", {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: {
            description,
            subject,
            topic: topic || description,
            language,
          },
        });

        if (cancelled) return;

        if (fnErr) {
          const ctx = (fnErr as any)?.context;
          let parsed: any = null;
          try {
            const txt = await ctx?.text?.();
            if (txt) parsed = JSON.parse(txt);
          } catch { /* ignore */ }

          if (parsed?.error === "premium_required" || ctx?.status === 403) {
            setPremiumRequired(true);
            setError(parsed?.message || "Illustration réservée aux abonnés Premium.");
          } else if (ctx?.status === 401) {
            setHidden(true);
          } else {
            setError("Illustration indisponible.");
          }
          setLoading(false);
          return;
        }

        if ((data as any)?.error === "premium_required") {
          setPremiumRequired(true);
          setError((data as any).message || "Illustration réservée aux abonnés Premium.");
        } else if ((data as any)?.url) {
          const fetched = (data as any).url as string;
          // Persist remote URL (lightweight) and the blob (heavy, offline-ready)
          const map = readCache();
          map[cacheKey] = { url: fetched, ts: Date.now() };
          writeCache(map);
          const blobUrl = await cacheImageFromUrl(cacheKey, fetched);
          setUrl(blobUrl || fetched);
        } else {
          setError("Illustration indisponible.");
        }
      } catch (e) {
        if (!cancelled) setError("Illustration indisponible.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [description, subject, topic, language, cacheKey, initialRemote]);

  if (hidden) return null;

  if (loading) {
    return (
      <div className="my-3 rounded-2xl bg-card border border-border p-4 flex items-center gap-3 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin text-primary" />
        <span>Génération de l'illustration…</span>
      </div>
    );
  }

  if (premiumRequired) {
    return (
      <div className="my-3 rounded-2xl border-2 border-dashed border-primary/30 bg-primary/5 p-4 flex items-start gap-3">
        <Lock className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-bold text-foreground mb-1">Illustration Premium</p>
          <p className="text-muted-foreground leading-relaxed">{error}</p>
        </div>
      </div>
    );
  }

  if (error || !url) {
    return (
      <div className="my-3 rounded-2xl bg-muted border border-border p-3 flex items-center gap-2 text-xs text-muted-foreground">
        <ImageIcon className="w-4 h-4" />
        <span>{error || "Illustration indisponible."}</span>
      </div>
    );
  }

  return (
    <figure className="my-3 rounded-2xl overflow-hidden bg-card border border-border">
      <img
        src={url}
        alt={description}
        loading="lazy"
        decoding="async"
        className="w-full h-auto max-h-[420px] object-contain bg-muted"
      />
    </figure>
  );
};

export default NotionIllustration;
